import type { Obj, Page } from "../../shared/types";

export type Seg = [number, number, number, number];

export const center = (o: Obj) => ({ x: o.x + o.w / 2, y: o.y + o.h / 2 });

/** Point in object's local, unrotated frame (relative to its top-left). */
export function toLocal(o: Obj, px: number, py: number) {
  const cx = o.x + o.w / 2, cy = o.y + o.h / 2;
  const a = (-(o.rot || 0) * Math.PI) / 180;
  const dx = px - cx, dy = py - cy;
  return { x: dx * Math.cos(a) - dy * Math.sin(a) + o.w / 2, y: dx * Math.sin(a) + dy * Math.cos(a) + o.h / 2 };
}

export function distToSeg(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1;
  const l2 = dx * dx + dy * dy;
  let t = l2 ? ((px - x1) * dx + (py - y1) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

export function hitObj(o: Obj, px: number, py: number, tol: number): boolean {
  if (o.type === "wall") {
    const p = o.pts || [];
    for (let i = 0; i + 3 < p.length; i += 2) if (distToSeg(px, py, p[i], p[i + 1], p[i + 2], p[i + 3]) <= tol) return true;
    return false;
  }
  const l = toLocal(o, px, py);
  const pad = o.type === "path" ? tol : 0;
  return l.x >= -pad && l.y >= -pad && l.x <= o.w + pad && l.y <= o.h + pad;
}

export function objBounds(o: Obj) {
  if (o.type === "wall") {
    const p = o.pts || [0, 0];
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
    for (let i = 0; i < p.length; i += 2) {
      x1 = Math.min(x1, p[i]); x2 = Math.max(x2, p[i]);
      y1 = Math.min(y1, p[i + 1]); y2 = Math.max(y2, p[i + 1]);
    }
    return { x1, y1, x2, y2 };
  }
  // rotated box → axis aligned
  const c = center(o);
  const a = ((o.rot || 0) * Math.PI) / 180;
  const cs = Math.abs(Math.cos(a)), sn = Math.abs(Math.sin(a));
  const hw = (o.w * cs + o.h * sn) / 2, hh = (o.w * sn + o.h * cs) / 2;
  return { x1: c.x - hw, y1: c.y - hh, x2: c.x + hw, y2: c.y + hh };
}

export function pointInPoly(x: number, y: number, pts: number[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 2; i < pts.length; j = i, i += 2) {
    const xi = pts[i], yi = pts[i + 1], xj = pts[j], yj = pts[j + 1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Wall segments that block sight (closed doors block, open doors don't). */
export function wallSegs(walls: Obj[]): Seg[] {
  const out: Seg[] = [];
  for (const w of walls) {
    if (w.door && w.open) continue;
    const p = w.pts || [];
    for (let i = 0; i + 3 < p.length; i += 2) out.push([p[i], p[i + 1], p[i + 2], p[i + 3]]);
  }
  return out;
}

// ray (ox,oy)+(dx,dy)*t vs segment → t or Infinity
function raySeg(ox: number, oy: number, dx: number, dy: number, s: Seg): number {
  const sx = s[2] - s[0], sy = s[3] - s[1];
  const den = dx * sy - dy * sx;
  if (Math.abs(den) < 1e-12) return Infinity;
  const t = ((s[0] - ox) * sy - (s[1] - oy) * sx) / den;
  const u = ((s[0] - ox) * dy - (s[1] - oy) * dx) / den;
  if (t < 0 || u < -1e-9 || u > 1 + 1e-9) return Infinity;
  return t;
}

const visCache = new Map<string, number[]>();
let cacheKeyWalls = "";

/**
 * Visibility polygon from (px,py) limited to a square of half-size `radius`,
 * blocked by `segs`. Returns flat [x,y,...] points ordered by angle.
 */
export function visibility(px: number, py: number, radius: number, segs: Seg[], wallsKey: string): number[] {
  if (wallsKey !== cacheKeyWalls) { visCache.clear(); cacheKeyWalls = wallsKey; }
  const key = `${Math.round(px)},${Math.round(py)},${Math.round(radius)}`;
  const hit = visCache.get(key);
  if (hit) return hit;
  const r = radius;
  const bx1 = px - r, by1 = py - r, bx2 = px + r, by2 = py + r;
  const local: Seg[] = [
    [bx1, by1, bx2, by1], [bx2, by1, bx2, by2], [bx2, by2, bx1, by2], [bx1, by2, bx1, by1],
  ];
  for (const s of segs) {
    if (Math.max(s[0], s[2]) < bx1 || Math.min(s[0], s[2]) > bx2 || Math.max(s[1], s[3]) < by1 || Math.min(s[1], s[3]) > by2) continue;
    local.push(s);
  }
  const angles: number[] = [];
  for (const s of local) {
    for (const [x, y] of [[s[0], s[1]], [s[2], s[3]]]) {
      const a = Math.atan2(y - py, x - px);
      angles.push(a - 1e-5, a, a + 1e-5);
    }
  }
  const pts: { a: number; x: number; y: number }[] = [];
  for (const a of angles) {
    const dx = Math.cos(a), dy = Math.sin(a);
    let best = Infinity;
    for (const s of local) {
      const t = raySeg(px, py, dx, dy, s);
      if (t < best) best = t;
    }
    if (best === Infinity) continue;
    pts.push({ a, x: px + dx * best, y: py + dy * best });
  }
  pts.sort((p, q) => p.a - q.a);
  const flat: number[] = [];
  for (const p of pts) flat.push(p.x, p.y);
  if (visCache.size > 400) visCache.clear();
  visCache.set(key, flat);
  return flat;
}

/** Grid distance between two points in page units. */
export function measure(page: Page, x1: number, y1: number, x2: number, y2: number): number {
  const dx = Math.abs(x2 - x1) / page.grid, dy = Math.abs(y2 - y1) / page.grid;
  let cells: number;
  switch (page.diagonal) {
    case "euclid": cells = Math.hypot(dx, dy); break;
    case "manhattan": cells = Math.round(dx) + Math.round(dy); break;
    case "pf": { const a = Math.round(dx), b = Math.round(dy); cells = Math.max(a, b) + Math.floor(Math.min(a, b) / 2); break; }
    default: cells = Math.max(Math.round(dx), Math.round(dy));
  }
  const v = cells * page.scale;
  return Math.round(v * 10) / 10;
}

export const unitsToPx = (page: Page, units: number) => (units / (page.scale || 5)) * page.grid;

/** Ramer–Douglas–Peucker simplification for freehand strokes. */
export function simplify(pts: number[], eps: number): number[] {
  if (pts.length <= 4) return pts;
  const n = pts.length / 2;
  const keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let maxD = 0, idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = distToSeg(pts[i * 2], pts[i * 2 + 1], pts[a * 2], pts[a * 2 + 1], pts[b * 2], pts[b * 2 + 1]);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > eps && idx > 0) { keep[idx] = 1; stack.push([a, idx], [idx, b]); }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i * 2], pts[i * 2 + 1]);
  return out;
}

/** Absolute points → normalized path object fields. */
export function pathFromPoints(abs: number[]): { x: number; y: number; w: number; h: number; pts: number[] } {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (let i = 0; i < abs.length; i += 2) {
    x1 = Math.min(x1, abs[i]); x2 = Math.max(x2, abs[i]);
    y1 = Math.min(y1, abs[i + 1]); y2 = Math.max(y2, abs[i + 1]);
  }
  const w = Math.max(1, x2 - x1), h = Math.max(1, y2 - y1);
  const pts: number[] = [];
  for (let i = 0; i < abs.length; i += 2) pts.push(+((abs[i] - x1) / w).toFixed(4), +((abs[i + 1] - y1) / h).toFixed(4));
  return { x: x1, y: y1, w, h, pts };
}
